/**
 * Welle 12 (pre139) / pre198: Gemeinsame Anruf-Steuerleiste für die Companion.
 * Ab pre198 als ICON-Toolbar im Stil der Berater-Videoleiste (.tb-btn), damit A/V-
 * und Companion-Ansicht optisch übereinstimmen. Rein präsentational + prop-getrieben.
 */
import type React from 'react';

export interface CallControlBarProps {
  micMuted: boolean;
  camOn: boolean;
  guestConnected: boolean;
  recording: boolean;
  recPending: boolean;
  topics: { id: string; label: string }[];
  recTopic: string;
  onSelectTopic: (id: string) => void;
  onToggleMic: () => void;
  onToggleCam: () => void;
  onKickGuest: () => void;
  onTransfer: () => void;
  onHangup: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  pipSupported?: boolean;
  pipActive?: boolean;
  onToggleDock?: () => void;
  // pre198: Bildschirmfreigabe in die Leiste integriert.
  screenSharing?: boolean;
  onToggleShare?: () => void;
  shareAvailable?: boolean;
  footer?: React.ReactNode;
}

function IconBtn(props: {
  onClick: () => void; title: string; active?: boolean; danger?: boolean; disabled?: boolean; children: React.ReactNode;
}) {
  const { onClick, title, active, danger, disabled, children } = props;
  const cls = 'tb-btn' + (danger ? ' muted' : active ? ' active' : '');
  return (
    <button
      className={cls}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{ opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {children}
      <span className="tb-btn-tooltip">{title}</span>
    </button>
  );
}

export function CallControlBar(props: CallControlBarProps) {
  const {
    micMuted, camOn, guestConnected, recording, recPending, topics, recTopic,
    onSelectTopic, onToggleMic, onToggleCam, onKickGuest, onTransfer, onHangup,
    onStartRecording, onStopRecording, pipSupported, pipActive, onToggleDock,
    screenSharing, onToggleShare, shareAvailable, footer,
  } = props;

  return (
    <div style={{ margin: '4px 0 18px', paddingTop: 12, borderTop: '1px solid var(--hairline)' }}>
      {/* Icon-Leiste */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'center',
          background: '#141414', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 999,
          padding: 5, width: 'fit-content', maxWidth: '100%', margin: '0 auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
      >
        <IconBtn onClick={onToggleMic} title={micMuted ? 'Mikrofon ein' : 'Mikrofon aus'} danger={micMuted}>
          {micMuted ? (
            <svg viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zM15 11.16V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l6 5.98zM4.27 3L3 4.27l6 6V11c0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" /></svg>
          ) : (
            <svg viewBox="0 0 24 24"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" /></svg>
          )}
        </IconBtn>

        <IconBtn onClick={onToggleCam} title={camOn ? 'Kamera aus' : 'Kamera ein'} danger={!camOn}>
          {camOn ? (
            <svg viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" /></svg>
          ) : (
            <svg viewBox="0 0 24 24"><path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z" /></svg>
          )}
        </IconBtn>

        {guestConnected && (
          <IconBtn onClick={onKickGuest} title="Experte trennen">
            <svg viewBox="0 0 24 24"><path d="M14 8c0-2.21-1.79-4-4-4S6 5.79 6 8s1.79 4 4 4 4-1.79 4-4zm3 2v2h6v-2h-6zM2 18v2h16v-2c0-2.66-5.33-4-8-4s-8 1.34-8 4z" /></svg>
          </IconBtn>
        )}

        <IconBtn onClick={onTransfer} title="Weiterleiten">
          <svg viewBox="0 0 24 24"><path d="M14 5v4H4v6h10v4l8-7-8-7z" /></svg>
        </IconBtn>

        {onToggleShare && (
          <IconBtn onClick={onToggleShare} title={screenSharing ? 'Freigabe beenden' : 'Bildschirm teilen'} active={screenSharing} danger={screenSharing} disabled={!shareAvailable && !screenSharing}>
            <svg viewBox="0 0 24 24"><path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6zm9 1.5l-4 4h2.5V15h3v-3.5H17l-4-4z" /></svg>
          </IconBtn>
        )}

        {pipSupported && onToggleDock && (
          <IconBtn onClick={onToggleDock} title={pipActive ? 'Angedockt' : 'Abdocken'} active={pipActive}>
            <svg viewBox="0 0 24 24"><path d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm0 16.01H3V4.98h18v14.03z" /></svg>
          </IconBtn>
        )}

        {topics.length > 0 && (
          <IconBtn
            onClick={recording ? onStopRecording : onStartRecording}
            title={recording ? 'Aufzeichnung beenden' : recPending ? 'Warte auf Einwilligung…' : 'Aufzeichnung starten'}
            active={recording}
            danger={recording}
            disabled={!recording && recPending}
          >
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="6" /></svg>
          </IconBtn>
        )}

        <span style={{ width: 1, height: 22, background: 'var(--hairline)', margin: '0 4px' }} />

        <button className="tb-hangup" onClick={onHangup} title="Auflegen" style={{ width: 38, height: 38 }}>
          <svg viewBox="0 0 24 24"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" /></svg>
        </button>
      </div>

      {recording && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#ef4444', marginTop: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />REC
        </div>
      )}
      {recPending && (
        <div style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>Warte auf Einwilligung des Gastes auf der Stele…</div>
      )}
      {footer ? (
        <div style={{ fontSize: 10.5, color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>{footer}</div>
      ) : null}
    </div>
  );
}
